import React, { ReactElement, ReactNode, useState } from "react";

import styled from "styled-components";

import { IconButton } from "../../components/IconButton";
import {
  AttachmentIcon,
  CircleIcon,
  ConnectionIcon,
  DrawIcon,
  InsertLinkIcon,
  ProjectIcon,
  ProjectStarIcon,
  RoundedRectangleIcon,
  ShapesIcon,
  NoteIcon,
  TaskIcon,
  TextIcon,
  TriangleIcon,
} from "../../components/Icons";
import { Tooltip } from "../../components/Tooltip";
import { Caption1 } from "../../components/Typography";

/* ------------------------------------------------------------------ */
/* Floating toolbar surfaces                                           */
/* ------------------------------------------------------------------ */

/* A floating, rounded toolbar surface (paper + shadow). */
export const ToolSurface = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px;
  border-radius: 14px;
  background-color: var(--page-paper-main);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.16);
  border: 1px solid var(--border-primary);

  /* A DS button colours its icon by declaring svg { fill: currentColor }, so
     every FILLED glyph follows the button's active and hover states for free.
     Three glyphs here are not filled: RoundedRectangleIcon and TriangleIcon are
     outlines with a stroke of var(--color-theme-600) baked into the path, and
     ProjectIcon draws its ruler as a stroked rect. Nothing about the button
     could ever reach them — an armed rectangle stayed exactly as grey as an
     unarmed one. This extends the same currentColor contract to a declared
     stroke.

     Scoped to direct children and to shapes inside a <g>, NOT to every
     descendant: ProjectIcon cuts its ruler with a <mask> whose <line> is
     stroked BLACK on purpose. In a mask only luminance matters, so recolouring
     that line would turn a clean diagonal cut into a half-transparent smear. */
  svg > [stroke],
  svg > g [stroke] {
    stroke: currentColor;
  }
`;

/* vertical separator used inside the toolbars */
export const verticalDivider = `
  .divider {
    width: 1px;
    height: 24px;
    background-color: var(--color-theme-400);
    margin: 0 6px;
    flex-shrink: 0;
  }
`;

export const StyledBottomDock = styled.div`
  position: absolute;
  left: 50%;
  bottom: 18px;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-width: calc(100% - 32px);
`;

/**
 * The floating second toolbar row — a small pill above the dock that holds the
 * members of one tool family (Circle / Rectangle / Triangle, or Task / Project).
 *
 * It is the same surface as the dock itself, so a family reads as an extension
 * of the button that opened it rather than as a different kind of UI. Only one
 * pill is ever up, because only one tool is ever armed.
 */
export const StyledToolPill = styled(ToolSurface)``;

/* Reserve two Caption1 lines (2 × 16px) so showing/hiding the hint never
   shifts the dock; visibility keeps the reserved space when hidden. */
export const StyledHint = styled(Caption1)`
  text-align: center;
  padding: 0 16px;
  min-height: 32px;

  &.hint-hidden {
    visibility: hidden;
  }
`;

/* Per-tool hint shown under the dock only while that tool is hovered. */
export const TOOL_HINTS = {
  circle:
    "Click to drop a default circle, or draw one to size. Press O for a circle.",
  roundedRectangle:
    "Click to drop a default rectangle, or draw one to size. Press R for a rectangle.",
  triangle:
    "Click to drop a default triangle, or draw one to size. Press Y for a triangle.",
  connector:
    "Drag from one element to another to connect them. Press C to connect.",
  projectElements:
    "Put a task or a project on the board — pick which above, then draw its card.",
  project:
    "Draw a box to place a project card, then pick the project it shows.",
  task: "Draw a box to place a task card, then pick the task it shows.",
  link: "Draw a box to place a link, or paste a URL anywhere. Press L for link.",
  file: "Draw a box to place a file, or drop one on the board. Press F for file.",
  sticky:
    "Click anywhere on the canvas to drop a sticky note. Press S for a sticky note.",
  text: "Click anywhere on the canvas to start typing. Press T for text.",
  draw: "Click and drag to draw freehand. Press D for the pen.",
  shapes:
    "Pick a shape above, then draw it on the board. Press R for a rectangle.",
} as const;

export type ToolHintKey = keyof typeof TOOL_HINTS;

export type ShapeTool = "circle" | "roundedRectangle" | "triangle";

/**
 * The armed board tool. Link, File, Project, Task and the three shapes are
 * wired — arming one turns the next canvas click into "drop the default-size
 * element here" and a drag into "draw one this big"; everything else stays a
 * mock, so the board is otherwise always in select mode.
 */
export type BoardTool =
  | "select"
  | "link"
  | "file"
  | "project"
  | "task"
  | ShapeTool;

export const SHAPE_TOOLS: readonly ShapeTool[] = [
  "circle",
  "roundedRectangle",
  "triangle",
];

export const isShapeTool = (tool: BoardTool): tool is ShapeTool =>
  (SHAPE_TOOLS as readonly string[]).includes(tool);

/**
 * What a dock button arms the instant its pill opens.
 *
 * Opening a family and choosing inside it are two separate decisions, and the
 * second one has a right answer often enough that asking for it every time is
 * just a toll: a shape is usually a rectangle, and the thing people put on a
 * board is usually a task. So the pill opens with that member already armed and
 * the gesture can start immediately — picking another member is a correction,
 * not a required step.
 */
export const DEFAULT_SHAPE_TOOL: ShapeTool = "roundedRectangle";
export const DEFAULT_PROJECT_ELEMENT_TOOL: BoardTool = "task";

/* What a plain click drops — mirrors DEFAULT_SHAPE_SIZES in
   new-activecollab-front, and is exactly what the hover preview promises. */
export const SHAPE_DEFAULT_SIZES: Record<
  ShapeTool,
  { width: number; height: number }
> = {
  roundedRectangle: { width: 100, height: 60 },
  circle: { width: 80, height: 80 },
  triangle: { width: 80, height: 80 },
};

/* Cards are auto-height, so their hover preview uses the app's nominal wire
   height (DEFAULT_LINK_CARD_SIZE / DEFAULT_FILE_CARD_SIZE). */
export const CARD_PREVIEW_HEIGHT = 240;

/** The kinds of card the draw gesture can place. */
export type DrawableTool = Exclude<BoardTool, "select">;

export interface ToolButtonProps {
  tip: string;
  active?: boolean;
  children: ReactNode;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export const ToolButton = ({
  tip,
  active,
  children,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: ToolButtonProps): ReactElement => (
  <Tooltip title={tip}>
    <IconButton
      variant="text gray"
      active={active}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </IconButton>
  </Tooltip>
);

export interface BottomDockProps {
  activeTool: BoardTool;
  onToolChange: (tool: BoardTool) => void;
}

/* Bottom-centre tool dock: the family pill (when one is open), the main
   toolbar, and the shared hint line. The dock is anchored by its BOTTOM edge,
   so a pill appearing or disappearing grows the stack upwards and never moves
   the toolbar itself. */
export const BottomDock = ({
  activeTool,
  onToolChange,
}: BottomDockProps): ReactElement => {
  const [hoverHint, setHoverHint] = useState<string | null>(null);

  const hoverProps = (tool: ToolHintKey): Record<string, () => void> => ({
    onMouseEnter: () => setHoverHint(TOOL_HINTS[tool]),
    onMouseLeave: () => setHoverHint(null),
  });

  /* An armed tool keeps its hint up after the pointer leaves the dock — the
     gesture happens on the canvas, so that is exactly when the instruction is
     needed. */
  const hint =
    hoverHint ?? (activeTool === "select" ? null : TOOL_HINTS[activeTool]);

  /* Which pill is up. Both are DERIVED from the armed tool rather than kept as
     their own open/closed state: opening a pill always arms one of its members,
     so "a member of this family is armed" and "this pill is showing" are the
     same fact. Deriving it is what makes every way of leaving the family close
     the pill for free — another dock tool, Esc on the canvas, or the gesture
     finishing and disarming the tool. Two states could disagree; one cannot. */
  const shapesOpen = isShapeTool(activeTool);
  const projectElementsOpen = activeTool === "task" || activeTool === "project";

  return (
    <StyledBottomDock>
      {/* Shapes pill — hidden until the Shape dock button opens it. Inside a
          pill a click is a plain choice, never a toggle off: the dock button is
          what closes the family, so clicking the armed shape again must not
          make the row that is being used disappear. */}
      {shapesOpen ? (
        <StyledToolPill>
          <ToolButton
            tip="Circle"
            active={activeTool === "circle"}
            onClick={() => onToolChange("circle")}
            {...hoverProps("circle")}
          >
            <CircleIcon />
          </ToolButton>
          <ToolButton
            tip="Rectangle"
            active={activeTool === "roundedRectangle"}
            onClick={() => onToolChange("roundedRectangle")}
            {...hoverProps("roundedRectangle")}
          >
            <RoundedRectangleIcon />
          </ToolButton>
          <ToolButton
            tip="Triangle"
            active={activeTool === "triangle"}
            onClick={() => onToolChange("triangle")}
            {...hoverProps("triangle")}
          >
            <TriangleIcon />
          </ToolButton>
        </StyledToolPill>
      ) : null}

      {/* Project Elements pill — the same surface, the same button size and
          spacing, in the same slot above the dock. Task comes first because it
          is what the button arms by default. */}
      {projectElementsOpen ? (
        <StyledToolPill>
          <ToolButton
            tip="Task"
            active={activeTool === "task"}
            onClick={() => onToolChange("task")}
            {...hoverProps("task")}
          >
            <TaskIcon />
          </ToolButton>
          <ToolButton
            tip="Project"
            active={activeTool === "project"}
            onClick={() => onToolChange("project")}
            {...hoverProps("project")}
          >
            <ProjectIcon />
          </ToolButton>
        </StyledToolPill>
      ) : null}

      <ToolSurface>
        {/* The tools that are still mocks arm nothing — but they do DISARM, so
            reaching for one closes whatever pill is up and ends the gesture the
            board was waiting for. A dead button that also leaves the previous
            tool armed would be the confusing half of "not implemented yet". */}
        <Tooltip title="Sticky Note">
          <IconButton
            variant="text gray"
            onClick={() => onToolChange("select")}
            {...hoverProps("sticky")}
          >
            <NoteIcon />
          </IconButton>
        </Tooltip>
        <ToolButton
          tip="Text"
          onClick={() => onToolChange("select")}
          {...hoverProps("text")}
        >
          <TextIcon />
        </ToolButton>
        <ToolButton
          tip="Draw"
          onClick={() => onToolChange("select")}
          {...hoverProps("draw")}
        >
          <DrawIcon />
        </ToolButton>
        {/* Opening the family and arming its default member are one click: the
            pill appears with Rectangle already armed, so the next thing the
            pointer does can be the drawing itself. */}
        <ToolButton
          tip="Shape"
          active={shapesOpen}
          onClick={() =>
            onToolChange(shapesOpen ? "select" : DEFAULT_SHAPE_TOOL)
          }
          {...hoverProps("shapes")}
        >
          <ShapesIcon />
        </ToolButton>
        <ToolButton
          tip="Connect"
          onClick={() => onToolChange("select")}
          {...hoverProps("connector")}
        >
          <ConnectionIcon />
        </ToolButton>
        <ToolButton
          tip="Project Elements"
          active={projectElementsOpen}
          onClick={() =>
            onToolChange(
              projectElementsOpen ? "select" : DEFAULT_PROJECT_ELEMENT_TOOL
            )
          }
          {...hoverProps("projectElements")}
        >
          <ProjectStarIcon />
        </ToolButton>
        <ToolButton
          tip="Link"
          active={activeTool === "link"}
          onClick={() =>
            onToolChange(activeTool === "link" ? "select" : "link")
          }
          {...hoverProps("link")}
        >
          <InsertLinkIcon />
        </ToolButton>
        <ToolButton
          tip="File"
          active={activeTool === "file"}
          onClick={() =>
            onToolChange(activeTool === "file" ? "select" : "file")
          }
          {...hoverProps("file")}
        >
          <AttachmentIcon />
        </ToolButton>
      </ToolSurface>

      <StyledHint color="tertiary" className={hint ? undefined : "hint-hidden"}>
        {hint ?? " "}
      </StyledHint>
    </StyledBottomDock>
  );
};
